import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import porfolio from '../assets/pdf/geo.pdf'
import profileImg from '../assets/images/geodev.png'
import { useProjects } from '../hooks/useProjects'
import './About.css'

const skills = [
  'React', 'Three.js', 'TypeScript', 'Node.js', 'Python', 'UI/UX',
  'Java', 'C++', 'PHP', 'Laravel', 'HTML', 'CSS', 'R',
]

const AboutHeader = ({ inView }) => {
  const container = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.15,
      },
    },
  }

  const line = {
    hidden: {
      opacity: 0,
      y: 36,
      filter: 'blur(10px)',
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 1,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  }

  return (
    <header className="classic-header classic-header--cinematic about-header">
      <span className="classic-eyebrow">Profile</span>

      <div className="about-header-stage">
        <motion.h1
          className="classic-title about-header-title"
          variants={container}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          <motion.span className="about-header-line" variants={line}>
            Evolving
          </motion.span>
          <motion.span className="about-header-line about-header-muted" variants={line}>
            through clarity.
          </motion.span>
        </motion.h1>
      </div>
    </header>
  )
}

const About = () => {
  const [animatedStats, setAnimatedStats] = useState([0, 0, 0])
  const [heroRevealed, setHeroRevealed] = useState(false)
  const { count: projectCount, loading: projectsLoading } = useProjects()

  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true, rootMargin: '0px 0px -10% 0px' })
  const [statsRef, statsInView] = useInView({ threshold: 0.1, triggerOnce: true })
  const isVisible = inView || heroRevealed

  useEffect(() => {
    const onReveal = () => setHeroRevealed(true)
    const onHide = () => setHeroRevealed(false)

    window.addEventListener('hero-about-reveal', onReveal)
    window.addEventListener('hero-about-hide', onHide)

    return () => {
      window.removeEventListener('hero-about-reveal', onReveal)
      window.removeEventListener('hero-about-hide', onHide)
    }
  }, [])

  useEffect(() => {
    if (!statsInView || projectsLoading) return undefined

    const statValues = [5, projectCount || 20, 10]
    const timer = setInterval(() => {
      setAnimatedStats((prev) =>
        prev.map((stat, i) => {
          if (stat < statValues[i]) {
            return statValues[i] < 10 ? stat + 0.1 : stat + 1
          }
          return stat
        })
      )
    }, 30)

    return () => clearInterval(timer)
  }, [statsInView, projectCount, projectsLoading])

  const stats = [
    { value: Math.floor(animatedStats[0]), label: 'Years Experience' },
    { value: Math.floor(animatedStats[1]), label: 'Projects Completed' },
    { value: Math.floor(animatedStats[2]), label: 'Happy Clients' },
  ]

  return (
    <section id="about" ref={ref} className="classic-section classic-section--white">
      <div className="classic-inner">
        <AboutHeader inView={isVisible} />

        <div className="about-layout">
          <motion.aside
            className="classic-card about-profile about-neon-card"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <img
              src={profileImg}
              alt="Geo"
              className="about-avatar"
            />
            <h2 className="about-name">Geo</h2>
            <p className="about-role">4th Year Student</p>
            <p className="about-location">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Notre Dame University
            </p>
            <p className="about-bio">
              Creating innovative digital solutions with cutting-edge technology.
            </p>

            <div className="about-actions">
              <a href={porfolio} download className="classic-btn">
                Download Resume
              </a>
              <button type="button" className="classic-btn classic-btn--outline" onClick={() => { window.location.href = '#contact' }}>
                Get in Touch
              </button>
              <button
                type="button"
                className="classic-btn classic-btn--outline"
                onClick={() => window.open('https://21geo.vercel.app/', '_blank')}
              >
                Custom Geo AI
                <span className="about-badge">New</span>
              </button>
            </div>
          </motion.aside>

          <div className="about-content">
            <motion.div
              className="classic-card about-neon-card"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className="classic-card-title">My Journey</h3>
              <p className="classic-card-text">
                As an Information Technology student at Notre Dame of Marbel University, I specialize in creating
                digital experiences that blend innovative technology with intuitive design. My expertise spans modern
                web development, software development, game development, and custom solutions.
              </p>
            </motion.div>

            <motion.div
              className="classic-card about-neon-card"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              <h3 className="classic-card-title">Technical Skills</h3>
              <div className="about-skills">
                {skills.map((skill) => (
                  <span key={skill} className="classic-pill">{skill}</span>
                ))}
              </div>
            </motion.div>

            <motion.div
              ref={statsRef}
              className="about-stats about-neon-card"
              initial={{ opacity: 0, y: 20 }}
              animate={statsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {stats.map((stat) => (
                <div key={stat.label} className="about-stat">
                  <p className="classic-stat-value">{stat.value}+</p>
                  <p className="classic-stat-label">{stat.label}</p>
                </div>
              ))}
            </motion.div>

            <motion.div
              className="classic-cta about-neon-card"
              initial={{ opacity: 0, y: 20 }}
              animate={statsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <div>
                <h3 className="classic-cta-title">Want to See My Projects?</h3>
                <p className="classic-cta-text">
                  Explore my portfolio and discover the innovative solutions I&apos;ve built.
                </p>
              </div>
              <button type="button" className="classic-btn" onClick={() => { window.location.href = '#showcase' }}>
                View Projects
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default About
